import type {
  AdminPassportOrder,
  LinkPassportOrderTagRequest,
  LinkTagToOrderRequest,
  ListPassportOrdersParams,
  PassportCardStock,
  PassportOrderPage,
  ShipPassportOrderRequest,
} from '@/types';
import { httpClient } from './httpClient';

const BASE = '/api/art/admin/passport-orders';

function orderUrl(id: string, tail = ''): string {
  return `${BASE}/${encodeURIComponent(id)}${tail}`;
}

// The client unwraps the answer envelope, so the next cursor is the id of the
// last order whenever the page is full (the server pages by order id).
export async function listPassportOrders(
  params: ListPassportOrdersParams = {},
): Promise<PassportOrderPage> {
  const response = await httpClient.get<AdminPassportOrder[]>(`${BASE}/`, { params });
  const items = response.data;
  const full = params.limit !== undefined && items.length >= params.limit;
  const last = items[items.length - 1];

  return { items, next: full && last ? last.id : undefined };
}

export async function getPassportOrderAdmin(id: string): Promise<AdminPassportOrder> {
  const response = await httpClient.get<AdminPassportOrder>(orderUrl(id));

  return response.data;
}

export async function getPassportCardStock(): Promise<PassportCardStock> {
  const response = await httpClient.get<PassportCardStock>(`${BASE}/stock`);

  return response.data;
}

export async function linkPassportOrderTag(
  id: string,
  body: LinkPassportOrderTagRequest,
): Promise<AdminPassportOrder> {
  const response = await httpClient.patch<AdminPassportOrder>(orderUrl(id, '/link'), body);

  return response.data;
}

export async function linkTagToOrder(
  cardUuid: string,
  body: LinkTagToOrderRequest,
): Promise<AdminPassportOrder> {
  const response = await httpClient.post<AdminPassportOrder>(
    `/api/art/admin/tags/${encodeURIComponent(cardUuid)}/link`,
    body,
  );

  return response.data;
}

export async function shipPassportOrder(
  id: string,
  body: ShipPassportOrderRequest,
): Promise<AdminPassportOrder> {
  const response = await httpClient.patch<AdminPassportOrder>(orderUrl(id, '/ship'), body);

  return response.data;
}

export interface ConfirmPassportTagRequest {
  card_uuid?: string;
  reason: string;
}

export async function approvePassportOrder(id: string): Promise<AdminPassportOrder> {
  const response = await httpClient.patch<AdminPassportOrder>(orderUrl(id, '/approve'));

  return response.data;
}

export async function rejectPassportOrder(
  id: string,
  body: { reason: string },
): Promise<AdminPassportOrder> {
  const response = await httpClient.patch<AdminPassportOrder>(orderUrl(id, '/reject'), body);

  return response.data;
}

export async function confirmPassportTag(
  id: string,
  body: ConfirmPassportTagRequest,
): Promise<AdminPassportOrder> {
  const response = await httpClient.patch<AdminPassportOrder>(orderUrl(id, '/confirm-tag'), body);

  return response.data;
}
